/**
 * Ciclo de vida Pro: fuentes (pago/cupón/admin) + actividad con/sin Pro.
 * RPC: get_pro_lifecycle_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';

export interface ProSourceCount {
    source: string;
    users: number;
}

export interface ProEndedUser {
    user_id: string;
    source: string;
    ended_at: string;
    active_days_with_pro: number;
    active_days_after_pro: number;
    sessions_with_pro: number;
    sessions_after_pro: number;
    captures_with_pro: number;
    captures_after_pro: number;
}

export interface ProLifecycleAnalytics {
    period_days: number;
    active_total: number;
    active_by_source: ProSourceCount[];
    new_in_period_by_source: ProSourceCount[];
    ended_in_period: number;
    ended_by_source: ProSourceCount[];
    post_pro_activity: {
        sample_users: number;
        avg_active_days_with_pro: number | null;
        avg_active_days_after_pro: number | null;
        avg_sessions_with_pro: number | null;
        avg_sessions_after_pro: number | null;
        avg_captures_with_pro: number | null;
        avg_captures_after_pro: number | null;
        still_active_after_7d_pct: number | null;
    };
    recent_ended: ProEndedUser[];
    source_labels: Record<string, string>;
}

function parseProLifecycle(raw: unknown): ProLifecycleAnalytics | null {
    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { return null; }
    }
    if (Array.isArray(payload)) payload = payload[0];
    if (payload && typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        if (obj.get_pro_lifecycle_analytics) payload = obj.get_pro_lifecycle_analytics;
    }
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    const post = (p.post_pro_activity && typeof p.post_pro_activity === 'object'
        ? p.post_pro_activity
        : {}) as Record<string, unknown>;

    return {
        period_days: Number(p.period_days ?? 30),
        active_total: Number(p.active_total ?? 0),
        active_by_source: Array.isArray(p.active_by_source) ? p.active_by_source as ProSourceCount[] : [],
        new_in_period_by_source: Array.isArray(p.new_in_period_by_source) ? p.new_in_period_by_source as ProSourceCount[] : [],
        ended_in_period: Number(p.ended_in_period ?? 0),
        ended_by_source: Array.isArray(p.ended_by_source) ? p.ended_by_source as ProSourceCount[] : [],
        post_pro_activity: {
            sample_users: Number(post.sample_users ?? 0),
            avg_active_days_with_pro: post.avg_active_days_with_pro == null ? null : Number(post.avg_active_days_with_pro),
            avg_active_days_after_pro: post.avg_active_days_after_pro == null ? null : Number(post.avg_active_days_after_pro),
            avg_sessions_with_pro: post.avg_sessions_with_pro == null ? null : Number(post.avg_sessions_with_pro),
            avg_sessions_after_pro: post.avg_sessions_after_pro == null ? null : Number(post.avg_sessions_after_pro),
            avg_captures_with_pro: post.avg_captures_with_pro == null ? null : Number(post.avg_captures_with_pro),
            avg_captures_after_pro: post.avg_captures_after_pro == null ? null : Number(post.avg_captures_after_pro),
            still_active_after_7d_pct: post.still_active_after_7d_pct == null ? null : Number(post.still_active_after_7d_pct),
        },
        recent_ended: Array.isArray(p.recent_ended) ? p.recent_ended as ProEndedUser[] : [],
        source_labels: (p.source_labels && typeof p.source_labels === 'object'
            ? p.source_labels as Record<string, string>
            : {}),
    };
}

export function useProLifecycleAnalytics(days: number = 30) {
    return useQuery<ProLifecycleAnalytics>({
        queryKey: ['admin', 'pro-lifecycle', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_pro_lifecycle_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const parsed = parseProLifecycle(data);
            if (!parsed) throw new Error('Respuesta inválida de get_pro_lifecycle_analytics');
            return parsed;
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function proSourceLabel(source: string, labels?: Record<string, string>): string {
    return labels?.[source] ?? source;
}
